import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Avatar,
  InputLabel,
  FormControl,
  Paper,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import { 
  Upload as UploadIcon, 
  Delete as DeleteIcon,
  PhotoCamera as CameraIcon,
  Palette as PaletteIcon 
} from '@mui/icons-material';
import unifiedApi, { buildApiUrl } from '../../utils/unifiedApi';

const TeamDetailsEdit = ({ open, onClose, teamId, leagueId, onSave, mode = 'edit' }) => {
  const isCreateMode = mode === 'create';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const fileInputRef = useRef(null);
  
  const [teamData, setTeamData] = useState({
    id: null,
    name: '',
    leagueId: leagueId || null,
    logoUrl: '',
    jerseyColorName: '',
    jerseyColorHex: '',
    jerseyUrl: ''
  });

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await unifiedApi.getTeamBasic(teamId);
      if (!data) {
        throw new Error('Failed to fetch team data');
      }
      setTeamData({
        // Fall back to teamId/teamName for PublicTeamDto compatibility
        id: data.id ?? data.teamId ?? teamId,
        name: data.name || data.teamName || '',
        leagueId: data.leagueId,
        logoUrl: data.logoUrl || '',
        jerseyColorName: data.jerseyColorName || '',
        jerseyColorHex: data.jerseyColorHex || '#1976d2',
        jerseyUrl: data.jerseyUrl || ''
      });
      setLogoPreview(data.logoUrl || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (open && teamId && !isCreateMode) {
      fetchTeamData();
    } else if (open && isCreateMode) {
      // Reset to blank form for create mode
      setTeamData({
        id: null,
        name: '',
        leagueId: leagueId || null,
        logoUrl: '',
        jerseyColorName: '',
        jerseyColorHex: '',
        jerseyUrl: ''
      });
      setLogoFile(null);
      setLogoPreview('');
      setUploadError('');
    } else if (!open) {
      // Reset state when dialog closes
      setLogoFile(null);
      setLogoPreview('');
      setUploadError('');
    }
  }, [open, teamId, isCreateMode, leagueId, fetchTeamData]);

  const handleInputChange = (field, value) => {
    setTeamData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file (PNG, JPG, GIF, etc.)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('File size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      setUploadError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
        // Clear the URL field since we're using a file now
        setTeamData(prev => ({ ...prev, logoUrl: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setUploadError('');
    setTeamData(prev => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      let logoUrl = teamData.logoUrl;
      
      // If there's a new file, upload it first
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        formData.append('type', 'team-logo');
        
        const uploadData = await unifiedApi.request('/api/upload', {
          method: 'POST',
          body: formData
        });
        
        if (!uploadData) {
          throw new Error('Failed to upload logo');
        }
        
        logoUrl = uploadData.url;
      }

      if (isCreateMode) {
        await unifiedApi.post('/api/teams', {
          name: teamData.name,
          leagueId: teamData.leagueId || leagueId,
          logoUrl: logoUrl || null,
          jerseyColorName: teamData.jerseyColorName || null,
          jerseyColorHex: teamData.jerseyColorHex || null,
          jerseyUrl: teamData.jerseyUrl || null
        });
      } else {
        await unifiedApi.put(`/api/teams/${teamId}`, {
          id: teamData.id ?? teamId,
          name: teamData.name,
          leagueId: teamData.leagueId,
          logoUrl: logoUrl,
          jerseyColorName: teamData.jerseyColorName,
          jerseyColorHex: teamData.jerseyColorHex,
          jerseyUrl: teamData.jerseyUrl
        });
      }

      onSave && onSave();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError('');
    setUploadError('');
    setLogoFile(null);
    setLogoPreview('');
    onClose();
  };

  // Fix logo display: if logoPreview is from file upload (data URL), use it directly
  // If logoPreview is a full URL (http/https) or data URL, use as-is
  // If a relative path, build the full API URL (buildApiUrl handles absolute URLs)
  const resolveLogoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    return buildApiUrl(url);
  };
  const currentLogo = logoPreview ? resolveLogoUrl(logoPreview) : resolveLogoUrl(teamData.logoUrl);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          {isCreateMode ? 'Add Team' : 'Edit Team Details'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {isCreateMode ? 'Set up your new team\'s details and branding' : 'Customize your team\'s appearance and branding'}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Basic Information Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    Basic Information
                  </Typography>
                  <TextField
                    label="Team Name"
                    fullWidth
                    required
                    value={teamData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={saving}
                    placeholder="Enter your team name"
                  />
                </Paper>
              </Grid>

              {/* Team Logo Section */}
              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CameraIcon />
                    Team Logo
                  </Typography>
                  
                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {uploadError}
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={currentLogo}
                      sx={{ width: 80, height: 80, border: '2px solid #e0e0e0' }}
                    >
                      {teamData.name?.charAt(0)?.toUpperCase() || 'T'}
                    </Avatar>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        onClick={handleUploadClick}
                        disabled={saving}
                        size="small"
                      >
                        Upload Logo
                      </Button>
                      {(currentLogo || logoFile) && (
                        <IconButton
                          onClick={handleRemoveLogo}
                          color="error"
                          disabled={saving}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                        OR
                      </Typography>
                      <TextField
                        label="Logo URL"
                        fullWidth
                        size="small"
                        value={teamData.logoUrl || ''}
                        onChange={(e) => {
                          handleInputChange('logoUrl', e.target.value);
                          setLogoPreview(e.target.value);
                          setLogoFile(null);
                        }}
                        disabled={saving || logoFile}
                        helperText="Enter a URL to your team's logo image"
                        placeholder="https://example.com/logo.png"
                      />
                    </Box>
                  </Box>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </Paper>
              </Grid>

              {/* Jersey Customization Section */}
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <PaletteIcon />
                    Jersey Customization
                  </Typography>
                  
                  <Grid container spacing={3} alignItems="flex-start">
                    {/* Left Column - Color Settings */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                          label="Jersey Color Name"
                          fullWidth
                          value={teamData.jerseyColorName || ''}
                          onChange={(e) => handleInputChange('jerseyColorName', e.target.value)}
                          disabled={saving}
                          placeholder="e.g. Royal Blue, Forest Green"
                          helperText="What do you call this color?"
                        />

                        <FormControl fullWidth>
                          <InputLabel shrink>Jersey Color</InputLabel>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                            <input
                              type="color"
                              value={teamData.jerseyColorHex || '#1976d2'}
                              onChange={(e) => handleInputChange('jerseyColorHex', e.target.value)}
                              disabled={saving}
                              style={{
                                width: '60px',
                                height: '40px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer'
                              }}
                            />
                            <TextField
                              fullWidth
                              value={teamData.jerseyColorHex || ''}
                              onChange={(e) => handleInputChange('jerseyColorHex', e.target.value)}
                              disabled={saving}
                              placeholder="#1976d2"
                              sx={{ flex: 1 }}
                            />
                          </Box>
                        </FormControl>
                      </Box>
                    </Grid>

                    {/* Right Column - Jersey Preview */}
                    <Grid item xs={12} md={6}>
                      {teamData.jerseyColorHex && (
                        <Card variant="outlined" sx={{ bgcolor: 'grey.50', height: 'fit-content' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Jersey Preview
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                              {/* T-shirt SVG */}
                              <svg width="120" height="120" viewBox="0 0 100 100">
                                <path
                                  d="M20 30 L20 20 Q20 15 25 15 L35 15 Q40 10 60 10 Q65 15 75 15 Q80 15 80 20 L80 30 L85 35 L85 90 Q85 95 80 95 L20 95 Q15 95 15 90 L15 35 Z"
                                  fill={teamData.jerseyColorHex}
                                  stroke="#333"
                                  strokeWidth="2"
                                />
                              </svg>
                              <Box>
                                <Typography variant="body1" fontWeight="medium">
                                  {teamData.jerseyColorName || 'Custom Color'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {teamData.jerseyColorHex}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || !teamData.name.trim()}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : isCreateMode ? 'Create Team' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamDetailsEdit;